import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomSpinner from './CustomSpinner';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';
import { useSignedInContactDetails } from '../../UtilityHookFunctions/LoggedInUser';

const inputClass =
  'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

/**
 * The single Contact Us form, rendered both as a page section and inside the
 * Clinic Profile modal. It draws no card or overlay of its own; the caller
 * supplies that chrome.
 *
 * Name, email and mobile come from the signed-in user rather than being asked
 * for again, so only the subject and message are typed here.
 */
export default function ContactUsForm({
  title = 'Contact Us',
  description = 'Your name and contact details are taken from your account.',
  submitLabel = 'Send Message',
  sendingLabel = 'Sending...',
  onSubmitted,
  onCancel,
}) {
  const signedInDetails = useSignedInContactDetails();
  const [formData, setFormData] = useState({ subject: '', query: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.query.trim()) {
      toast.error('Please enter your query or message', { toastId: 'contact-query-req' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${END_POINT}/mail/contact-us`,
        { ...signedInDetails, ...formData, sourcePage: window.location.pathname },
        { headers: { Authorization: getJwtToken() || '' } }
      );
      toast.success(res?.data?.msg || 'Message delivered to admin successfully!', {
        toastId: 'contact-msg-sent',
      });
      setFormData({ subject: '', query: '' });
      if (onSubmitted) onSubmitted();
    } catch (err) {
      console.error('Failed to send contact inquiry:', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to send message. Please try again.',
        { toastId: 'contact-msg-err' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="border-b border-slate-100 pb-4 mb-6">
        <h3 className="text-xl font-bold text-slate-800">{title}</h3>
        <p className="text-xs text-slate-500 mt-1">{description}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 text-left">
        <div>
          <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
            Subject
          </label>
          <input
            type="text"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="e.g. Clinic software inquiry / Feature request"
            className={inputClass}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
            Query / Message <span className="text-rose-500">*</span>
          </label>
          <textarea
            name="query"
            rows={4}
            value={formData.query}
            onChange={handleChange}
            placeholder="Write your message or inquiry here..."
            required
            className={`${inputClass} resize-y`}
          />
        </div>

        <div className="pt-2 flex justify-end gap-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2.5 text-sm text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition flex items-center gap-2 shadow"
          >
            {submitting ? (
              <>
                <CustomSpinner height={16} width={16} />
                <span>{sendingLabel}</span>
              </>
            ) : (
              <span>{submitLabel}</span>
            )}
          </button>
        </div>
      </form>
    </>
  );
}
